import React, { useState, useMemo } from "react";
import "../management/PremiumGrid.css";
import "./MaintenanceView.css";
import { Vehicle } from "../data/database";

export type SaleStatus = "자산처분 승인" | "최종 결재 진행중" | "처분 완료";

export interface SalesRecord {
    id: string; // 차량 번호
    model: string;
    depot: string;
    driver: string;
    saleStatus: SaleStatus;
    estimatedValue: number; // reference for used car
    usedCarValue: number;
    remanCost: number; // cost to replace
    soh: number;
    accidentHistory: boolean;
    phone: string;
}

const INITIAL_SALES_RECORDS: SalesRecord[] = [
    {
        id: "12가 1025",
        model: "기아 봉고III EV",
        depot: "부산",
        driver: "흰이인",
        saleStatus: "자산처분 승인",
        estimatedValue: 15500000,
        usedCarValue: 15500000,
        remanCost: 4500000,
        soh: 88,
        accidentHistory: false,
        phone: "010-1234-5678"
    },
    {
        id: "12가 1043",
        model: "현대 포터II 일렉트릭",
        depot: "인천",
        driver: "오세원",
        saleStatus: "자산처분 승인",
        estimatedValue: 8000000,
        usedCarValue: 8000000,
        remanCost: 4500000,
        soh: 78,
        accidentHistory: true,
        phone: "010-2345-6789"
    },
    {
        id: "12가 1076",
        model: "기아 봉고III EV",
        depot: "대구",
        driver: "윤이인",
        saleStatus: "자산처분 승인",
        estimatedValue: 14200000,
        usedCarValue: 14200000,
        remanCost: 4500000,
        soh: 85,
        accidentHistory: false,
        phone: "010-3456-7890"
    },
    {
        id: "12가 1109",
        model: "현대 포터II 일렉트릭",
        depot: "광주",
        driver: "윤훈기",
        saleStatus: "자산처분 승인",
        estimatedValue: 5000000,
        usedCarValue: 5000000,
        remanCost: 4500000,
        soh: 70,
        accidentHistory: true,
        phone: "010-4567-8901"
    }
];

// Mock data for eligible vehicles to add
const ELIGIBLE_VEHICLES = [
    { id: "45허 8912", model: "현대 아이오닉5", depot: "서울", driver: "김철수", soh: 72, mileage: 154000 },
    { id: "12가 3456", model: "기아 봉고III EV", depot: "서울", driver: "박영희", soh: 65, mileage: 210000 },
    { id: "88나 5678", model: "현대 포터II 일렉트릭", depot: "부산", driver: "이민수", soh: 80, mileage: 110000 },
    { id: "34다 9012", model: "기아 니로 EV", depot: "인천", driver: "최지원", soh: 55, mileage: 280000 }
];

// AddDisposalDrawer (Inline in SalesValuationView instead of a separate component for easier state management, or separate if preferred. Let's keep it inline for simplicity and drawer structure).

export default function SalesValuationView() {
    const [records, setRecords] = useState<SalesRecord[]>(INITIAL_SALES_RECORDS);
    const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
    const [isFinalApprovalModalOpen, setIsFinalApprovalModalOpen] = useState(false);
    const [selectedDisposalMethod, setSelectedDisposalMethod] = useState<string>("중고차 일괄 매각");
    const [finalApprovalEmail, setFinalApprovalEmail] = useState("");

    React.useEffect(() => {
        const approved: SalesRecord[] = JSON.parse(localStorage.getItem('disposal_approvals') || '[]');
        if (approved.length > 0) {
            setRecords(prev => {
                const existingIds = new Set(prev.map(r => r.id));
                const newRecords = approved.filter(a => !existingIds.has(a.id));
                return [...prev, ...newRecords];
            });
        }
    }, []);

    const selectedRecord = records.find(r => r.id === selectedRecordId) || null;
    const isDrawerOpen = Boolean(selectedRecord);

    const totalAssets = records.length;
    const pendingSales = records.filter(r => r.saleStatus === "최종 결재 진행중").length;
    const pendingRemans = records.filter(r => r.saleStatus === "처분 완료").length;
    const unregistered = records.filter(r => r.saleStatus === "자산처분 승인").length;
    
    // New meaningful KPI calculations
    const maxPossibleRevenue = records.reduce((sum, r) => sum + r.usedCarValue, 0);
    const vehiclesNeedingBattery = records.filter(r => r.soh < 80).length;

    const handleRegisterAction = (action: SaleStatus) => {
        if (!selectedRecord) return;
        setRecords(prev => prev.map(r => r.id === selectedRecord.id ? { ...r, saleStatus: action } : r));
    };

    return (
        <div className="vm-page">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                <div className="soh-header-left">
                    <h2 className="soh-header-title">자산 처분</h2>
                    <div className="soh-header-sub">노후 차량 및 성능 저하 배터리 차량의 데이터 기반 매각가 및 교체 비용 비교를 통한 수익 극대화.</div>
                </div>
            </div>

            {/* Registration KPIs */}
            <div className="maintenance-kpi-bar" style={{ gap: '12px' }}>
                <div className="m-kpi-card" style={{ flex: 1 }}>
                    <div className="m-kpi-label">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                        </svg>
                        처분 심사 대상 차량
                    </div>
                    <div className="m-kpi-value" style={{ color: '#1e3a8a' }}>
                        {totalAssets} <span className="m-kpi-sub" style={{ color: '#60a5fa' }}>대</span>
                    </div>
                </div>

                <div className="m-kpi-card" style={{ flex: 1 }}>
                    <div className="m-kpi-label">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="1" x2="12" y2="23"></line>
                            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                        </svg>
                        예상 최대 매각 수익
                    </div>
                    <div className="m-kpi-value" style={{ color: '#047857' }}>
                        {(maxPossibleRevenue / 10000).toLocaleString()} <span className="m-kpi-sub" style={{ color: '#34d399' }}>만원</span>
                    </div>
                </div>

                <div className="m-kpi-card" style={{ flex: 1 }}>
                    <div className="m-kpi-label">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f43f5e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                            <line x1="12" y1="9" x2="12" y2="13"></line>
                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                        </svg>
                        배터리 교체 권장 차량
                    </div>
                    <div className="m-kpi-value" style={{ color: '#be123c' }}>
                        {vehiclesNeedingBattery} <span className="m-kpi-sub" style={{ color: '#fda4af' }}>대 (SoH 80% 미만)</span>
                    </div>
                </div>

                <div className="m-kpi-card" style={{ flex: 1.5, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div className="m-kpi-label">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21.21 15.89A10 10 0 1 1 8 2.83"></path>
                            <path d="M22 12A10 10 0 0 0 12 2v10z"></path>
                        </svg>
                        처분 의사결정률
                    </div>
                    <div style={{ display: 'flex', width: '100%', height: '8px', borderRadius: '4px', overflow: 'hidden', marginTop: '4px' }}>
                        <div style={{ width: `${(pendingSales / totalAssets) * 100}%`, background: '#3b82f6' }}></div> {/* Used Car = Blue */}
                        <div style={{ width: `${(pendingRemans / totalAssets) * 100}%`, background: '#10b981' }}></div> {/* Reman = Green */}
                        <div style={{ width: `${(unregistered / totalAssets) * 100}%`, background: '#e2e8f0' }}></div> {/* Unregistered = Gray */}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 600 }}>
                        <span style={{ color: '#3b82f6' }}>결재 대기: {pendingSales}대</span>
                        <span style={{ color: '#10b981' }}>처분 완료: {pendingRemans}대</span>
                        <span style={{ color: '#94a3b8' }}>자산처분 승인: {unregistered}대</span>
                    </div>
                </div>
            </div>

            {/* Valuation Grid */}
            <div className="premium-grid-container" style={{ flex: 1, marginTop: '24px' }}>
                <div className="driving-toolbar" style={{ marginTop: '0', border: '1px solid #e2e8f0', borderBottom: 'none', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                    <div className="driving-toolbar-left" style={{ gap: '16px' }}>
                        <div className="driving-section-title">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                            </svg>
                            자산 처분 관리
                            <button className="btn-refresh" title="새로고침" onClick={() => window.location.reload()}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="23 4 23 10 17 10"></polyline>
                                    <polyline points="1 20 1 14 7 14"></polyline>
                                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                                </svg>
                            </button>
                        </div>
                        <div className="driving-tabs">
                            <button className="driving-tab-btn active">전체</button>
                            <button className="driving-tab-btn">자산처분 승인</button>
                            <button className="driving-tab-btn">결재 진행중</button>
                            <button className="driving-tab-btn">처분 완료</button>
                        </div>
                    </div>
                    
                    <div className="driving-toolbar-right">
                        <button className="driving-action-btn green-solid" onClick={() => alert("데이터 내보내기 진행중...")}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                                <line x1="8" y1="13" x2="16" y2="13"></line>
                                <line x1="8" y1="17" x2="16" y2="17"></line>
                                <polyline points="10 9 9 9 8 9"></polyline>
                            </svg>
                            데이터 내보내기
                        </button>

                        <div className="driving-search-wrapper">
                            <input
                                type="text"
                                placeholder="차량 번호 검색"
                                className="driving-search-input"
                            />
                            <svg className="driving-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8"></circle>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                            </svg>
                        </div>
                    </div>
                </div>

                <div className="premium-grid-wrapper" style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                    <table className="premium-grid">
                        <thead>
                            <tr>
                                <th style={{ width: '40px' }}><input type="checkbox" className="grid-checkbox" /></th>
                                <th>차량정보</th>
                                <th>SoH (%)</th>
                                <th>주요 사고 이력</th>
                                <th>중고차 예상가 (매각)</th>
                                <th style={{ textAlign: 'center' }}>재제조 배터리 팩 (교체)</th>
                                <th>진행 상태</th>
                            </tr>
                        </thead>
                        <tbody>
                            {records.map((record) => (
                                <tr
                                    key={record.id}
                                    className={`clickable-row ${selectedRecordId === record.id ? 'selected' : ''}`}
                                    onClick={() => setSelectedRecordId(record.id)}
                                >
                                    <td><input type="checkbox" className="grid-checkbox" checked={selectedRecordId === record.id} readOnly /></td>
                                    <td>
                                        <div className="grid-icon-text">
                                            <div className="grid-avatar">{record.driver.charAt(0)}</div>
                                            <div>
                                                <div style={{ fontWeight: 600, color: '#0f172a' }}>
                                                    {record.id}
                                                    <span className="grid-badge" style={{ background: '#e0f2fe', color: '#0284c7', fontSize: '11px', padding: '1px 4px', borderRadius: '3px', marginLeft: '6px', fontWeight: 600 }}>{record.depot}</span>
                                                </div>
                                                <div style={{ fontSize: '13px', color: '#64748b' }}>{record.model}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td><span style={{ fontWeight: 600, color: record.soh > 80 ? '#059669' : '#ca8a04' }}>{record.soh}%</span></td>
                                    <td>
                                        {record.accidentHistory ? (
                                            <span className="grid-badge error">골격 손상 발생</span>
                                        ) : (
                                            <span className="grid-badge success">무사고</span>
                                        )}
                                    </td>
                                    <td style={{ color: '#3b82f6', fontWeight: 600 }}>{(Math.floor(record.usedCarValue / 10000) - 50).toLocaleString()} ~ {(Math.floor(record.usedCarValue / 10000) + 50).toLocaleString()}만 원</td>
                                    <td style={{ color: '#94a3b8', fontWeight: 600, textAlign: 'center' }}>-</td>
                                    <td>
                                        <span className={`grid-badge ${record.saleStatus === '자산처분 승인' ? 'neutral' : record.saleStatus === '최종 결재 진행중' ? 'primary' : 'success'}`} style={{ fontWeight: 600 }}>
                                            {record.saleStatus === '자산처분 승인' ? '심사 대기' : record.saleStatus}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Drawer */}
            <div className={`vm-backdrop ${isDrawerOpen ? "open" : ""}`} onClick={() => setSelectedRecordId(null)}></div>
            <aside className={`vm-form-container ${isDrawerOpen ? "open" : ""}`}>
                {selectedRecord && (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <div>
                                <h3 className="vm-form-title" style={{ margin: 0 }}>처분 방식 선택 및 상세 정보</h3>
                            </div>
                            <button onClick={() => setSelectedRecordId(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}>&times;</button>
                        </div>

                        <div className="vm-form-body" style={{ marginTop: '16px' }}>

                            {/* Summary */}
                            <div className="m-drawer-section">
                                <div className="m-drawer-label">대상 차량 정보</div>
                                <div className="grid-icon-text" style={{ marginTop: '8px' }}>
                                    <div className="grid-avatar" style={{ width: '36px', height: '36px', fontSize: '14px' }}>{selectedRecord.driver.charAt(0)}</div>
                                    <div>
                                        <div className="m-drawer-value">{selectedRecord.id} ({selectedRecord.driver})</div>
                                        <div style={{ fontSize: '12px', color: '#64748b' }}>{selectedRecord.model} · SoH: {selectedRecord.soh}%</div>
                                    </div>
                                </div>
                            </div>

                            {/* Asset Financials */}
                            <div className="m-drawer-section">
                                <div className="m-drawer-label" style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M2 12h4l3-9 5 18 3-9h5"></path>
                                    </svg>
                                    AI 기반 처분 가치 산출
                                </div>

                                {/* Option 1: Used Car Sale */}
                                <div style={{ padding: '12px', border: '1px solid #e2e8f0', borderRadius: '6px', marginBottom: '12px', background: '#f8fafc' }}>
                                    <div style={{ fontWeight: 600, color: '#334155', fontSize: '14px', marginBottom: '6px' }}>Opt 1. 중고차 일괄 매각</div>
                                    <div style={{ fontWeight: 700, color: '#3b82f6', fontSize: '16px' }}>{(Math.floor(selectedRecord.usedCarValue / 10000) - 50).toLocaleString()} ~ {(Math.floor(selectedRecord.usedCarValue / 10000) + 50).toLocaleString()} 만원 <span style={{fontSize:'13px', fontWeight:500, color:'#64748b'}}>(예상 범위)</span></div>
                                </div>

                                {/* Option 2: Remanufacturing Replacement */}
                                <div style={{ padding: '12px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#f8fafc', opacity: 0.6 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <div style={{ fontWeight: 600, color: '#334155', fontSize: '14px' }}>Opt 2. 배터리 팩 재제조 교체</div>
                                        <div style={{ fontWeight: 700, color: '#94a3b8', fontSize: '14px' }}>비용 미확정</div>
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                                        <span>재제조 배터리 팩 비용</span>
                                        <span>-</span>
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#94a3b8', display: 'flex', justifyContent: 'space-between', marginTop: '2px' }}>
                                        <span>기존 폐배터리 매각 공제액</span>
                                        <span>-</span>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="m-drawer-section" style={{ background: 'transparent', borderColor: 'transparent', border: 'none', paddingTop: 0 }}>
                                {selectedRecord.saleStatus !== "자산처분 승인" ? (
                                    <div style={{ textAlign: 'center', padding: '16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', color: '#10b981', fontSize: '14px', fontWeight: 600 }}>
                                        처분 완료
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button
                                            className="driving-action-btn primary"
                                            onClick={() => setIsFinalApprovalModalOpen(true)}
                                            style={{ margin: 0, justifyContent: 'center', flex: 2, background: '#3b82f6', borderColor: '#3b82f6', color: '#fff' }}>
                                            최종 처분 요청
                                        </button>
                                        <button
                                            className="driving-action-btn neutral"
                                            onClick={() => {
                                                if (window.confirm("해당 차량의 자산 처분을 취소하고 목록에서 제거하시겠습니까?")) {
                                                    const currentApproved = JSON.parse(localStorage.getItem('disposal_approvals') || '[]');
                                                    const updatedApproved = currentApproved.filter((r: any) => r.id !== selectedRecord.id);
                                                    localStorage.setItem('disposal_approvals', JSON.stringify(updatedApproved));
                                                    
                                                    setRecords(prev => prev.filter(r => r.id !== selectedRecord.id));
                                                    setSelectedRecordId(null);
                                                }
                                            }}
                                            style={{ margin: 0, justifyContent: 'center', flex: 1, backgroundColor: 'white', borderColor: '#cbd5e1', color: '#475569' }}>
                                            처분 취소
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </aside>

            {/* 최종 결재 이메일 요청 모달 */}
            {isFinalApprovalModalOpen && selectedRecord && (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '420px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#0f172a' }}>최종 처분 요청</h3>
                    <button onClick={() => setIsFinalApprovalModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}>&times;</button>
                  </div>
                  
                  <div style={{ padding: '24px 20px' }}>
                    <p style={{ fontSize: '14px', color: '#334155', marginBottom: '16px' }}>
                      차량 <b>{selectedRecord.id}</b>의 최종 처분 결정을 선택하고 최종 결재권자에게 승인을 요청하시겠습니까?
                    </p>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>최종 결재권자 이메일 주소 (필수)</label>
                      <input
                        type="email"
                        placeholder="approver@example.com"
                        value={finalApprovalEmail}
                        onChange={(e) => setFinalApprovalEmail(e.target.value)}
                        style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                    </div>


                    <div style={{ marginBottom: '8px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>최종 처분 방식 선택</label>
                      <select 
                        value={selectedDisposalMethod}
                        onChange={(e) => setSelectedDisposalMethod(e.target.value)}
                        style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                      >
                        <option value="중고차 일괄 매각">중고차 일괄 매각 (예상 수익: {(Math.floor(selectedRecord.usedCarValue/10000) - 50).toLocaleString()} ~ {(Math.floor(selectedRecord.usedCarValue/10000) + 50).toLocaleString()}만원)</option>
                        <option value="배터리 팩 재제조 교체" disabled>배터리 팩 재제조 교체 (비용 미정 - 현재 선택 불가)</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', padding: '16px 20px', borderTop: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                    <button style={{ flex: 1, padding: '10px', backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 500, color: '#475569' }} onClick={() => setIsFinalApprovalModalOpen(false)}>취소</button>
                    <button 
                      style={{ flex: 1, padding: '10px', backgroundColor: '#3b82f6', border: '1px solid #3b82f6', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 500, color: 'white' }}
                      onClick={() => {
                        if (!finalApprovalEmail.trim()) {
                            alert("최종 결재권자 이메일을 입력해주세요.");
                            return;
                        }
                        handleRegisterAction("처분 완료");
                        alert(`[${finalApprovalEmail}] 로 최종 승인 요청 발송이 무사히 완료되었습니다!\n\n[데모 목적 가이드]\n결재권자가 이메일을 통해 최종 처분을 승인하여, 해당 차량이 '처분 완료' 처리되었습니다.\n*결정된 최종 처분 방식: ${selectedDisposalMethod}`);
                        setIsFinalApprovalModalOpen(false);
                        setFinalApprovalEmail("");
                      }}
                    >
                      최종 승인 요청 발송
                    </button>
                  </div>
                </div>
              </div>
            )}
        </div>
    );
}
